import React from "react";
import { useRouter } from "next/router";

import { styled } from "@styles/theme";

import Header from "@components/common/Header/HeaderUnlogged";
import { H1 } from "@components/common/Headings";
import { Box, Flex } from "@components/common/ResponsiveBox";
import { BackButton } from "@components/common/BackBtn";

const Container = styled.div`
  width: 100%;
  overflow: hidden;
  min-height: 100vh;
  background: linear-gradient(266.65deg, #edf2f7 22%, #ffffff 48%);

  small {
    display: block;
    color: ${({ theme }) => theme.colors.primary[300]};
    margin: 50px 0 30px;
  }

  p {
    font-size: 16px;
    color: ${({ theme }) => theme.colors.primary[300]};
    line-height: 28px;
    margin-bottom: 30px;
  }

  h2 {
    font-weight: 600;
    margin-bottom: 30px;
  }

  h3 {
    font-weight: 600;
    margin-bottom: 30px;
  }

  a {
    text-decoration: none;
  }
`;

const TermsOfService = () => {
  const router = useRouter();

  return (
    <Container>
      <Flex
        mX="auto"
        maxW={1200}
        mT={{ 0: "md", 2: "xxxg" }}
        pX={{ 0: "md", 3: "none" }}
      >
        <Header dark_logo />

        <Box width="90%" maxW={690} pY="xhg" mX="auto">
          <BackButton onPress={() => router.back()}>Back</BackButton>
          <H1 weight={600} align="center" mT="md">
            Terms of Service
          </H1>

          <small>Last updated: April 28, 2021</small>
          <p>
            This Subscription Service Agreement (“AGREEMENT”) is made and
            entered into the entity or person placing an order or accessing the
            Solutions (as defined below) (“Client”) and ACCENTURE DO BRASIL
            LTDA(“Accenture”). Accenture and Client are also each referred to
            under this Agreement individually as a “Party,” and together as the
            “Parties.”
          </p>

          <p>
            If you are entering into this Agreement on behalf of a company or
            other legal entity, you represent that you have the authority to
            bind such entity to this Agreement in which case the term “Client”
            shall refer to such entity. If you do not have such authority, or if
            you do not agree to the terms of this Agreement, do not accept this
            Agreement or use the Services.
          </p>

          <p>
            By using the Services, you are aware that the terms and conditions
            of this Agreement will apply to such Services.
          </p>

          <h2>1. AGREEMENT STRUCTURE AND INTERPRETATION</h2>

          <p>
            1.1. This Agreement permits Client to purchase a subscription to
            Service and sets out the general terms and conditions for the
            provision of the Services by Accenture (inclusive of Affiliates), as
            further defined hereunder.
          </p>

          <p>
            1.2. The capitalized terms as used in this Agreement have the
            meanings set out in the body of this Agreement or in Exhibit A
            (Definitions).
          </p>

          <h2>2. CLIENT’S RIGHTS AND OBLIGATIONS</h2>

          <p>
            2.1. Subject to the terms and conditions of this Agreement and the
            relevant Service Order, Accenture hereby grants to Client a
            non-exclusive, non-transferable right during the term of the
            Subscription to permit its Authorized Users to access and use the
            Services for the Permitted Purpose.
          </p>

          <p>
            2.2. Client acknowledges and agrees that it is solely responsible
            for (i) determining the suitability of the Services for its purposes
            and those of its Authorized Users, and (ii) ensuring that the way
            that it and its Authorized Users access and use the Services
            complies with any applicable laws.
          </p>

          <p>
            2.3. Client agrees that, unless explicitly authorized pursuant to
            the Permitted Purpose, it shall not license, sublicense, sell,
            resell, transfer, assign, distribute or otherwise commercially
            exploit the Services by making them available for access or use by
            any third party (except Authorized Users), including by means of
            operating a service bureau, outsourcing or time-sharing service.
          </p>

          <p>
            2.4. Client shall be solely responsible for ensuring that Client’s
            or its Authorized Users’ use of the Services shall not exceed any
            restrictions contained in this Agreement or the applicable Service
            Order.
          </p>

          <p>
            2.5. Client is responsible for all use of the Services by those who
            have access to them through Client’s credential, and for ensuring
            that its Authorized Users do not circumvent or disclose any
            usernames, passwords or other access credentials or authentication
            details, or interfere with or disrupt any other security control of
            the Platform Services.
          </p>

          <p>
            2.6. Client shall maintain commercially reasonable security
            standards for its and its Authorized Users’ use of the Services.
            Specifically, Client will use good industry practice virus
            protection software, and other customary procedures to screen any
            Client Content to avoid introducing any Virus or other malicious
            files or other harmful code that could disrupt the proper operation
            of the systems used in the provision of the Platform Services.
            Client also agrees that it shall use all reasonable endeavors to
            ensure that its Authorized Users do not upload or distribute files
            that contain Viruses, or do anything else to disrupt or attempt to
            disrupt, the systems and networks used for the provision of the
            Platform Services.
          </p>

          <p>
            2.7. Client shall be solely responsible for the acts and omissions
            of its Authorized Users as if they were the acts and omissions of
            Client, and for ensuring that anyone who uses the Services does so
            in accordance with the terms and conditions of this Agreement. In
            particular, the Client agrees that it shall not, and that it shall
            ensure that its Authorized Users do not: (i) access or use the
            Services to host or transmit any content, data or information that
            is illegal or which infringes any third party’s rights, such as
            intellectual property rights or right of privacy, or which otherwise
            violates any applicable laws; (ii) copy, translate, make derivative
            works, disassemble, decompile, reverse engineer or otherwise attempt
            to discover the source code or underlying ideas or algorithms
            embodied in the software applications or other systems used for the
            provision of the Services (including the IaaS), unless expressly
            permitted under any applicable laws, or remove any titles or
            trademarks, copyrights or restricted rights notices in the systems,
            software and other materials used in the provision of Services; or
            (iii) access or use the Services for the purpose of building
            competitive products or services by copying its features or user
            interface or by allowing a direct competitor of Accenture to access
            or use the Platform Services.
          </p>

          <p>
            2.8 Client will notify Accenture immediately if it becomes aware of
            any breach or threatened breach of the terms of this Section 2, or
            of any breach or threatened breach of security including any attempt
            by a third party to gain unauthorized access to the systems used for
            the provision of the Platform Services.
          </p>

          <p>
            2.9. Client acknowledges and agrees that it is responsible for
            obtaining and maintaining all hardware, software, communications
            equipment and network connections necessary to access and use the
            Platform Services, and for paying any applicable third-party fees
            and charges incurred while accessing and using the Platform
            Services.
          </p>

          <h2>3. ACCENTURE’S RIGHTS AND OBLIGATIONS</h2>

          <p>
            3.1. Accenture shall provide the Services in accordance with this
            Agreement.{" "}
          </p>

          <p>
            3.2. In providing the Services Accenture will use reasonable and
            appropriate technical and organizational security measures intended
            to safeguard Client Content against accidental, unauthorized or
            unlawful access, loss, damage or destruction.
          </p>

          <p>
            3.3. Accenture may need to apply updates or make changes to the
            Services, provided always that Accenture shall not be entitled to
            apply such updates or other changes in a manner that would make the
            Services non-conforming with the applicable service descriptions, or
            otherwise materially diminish the scope or the quality of the
            service provided, unless such changes are necessary for Accenture to
            comply with any applicable laws. Accenture will notify the Client
            reasonably in advance of any planned changes to the Services that
            have any material impact on Client’s access to or use of the
            relevant Service.
          </p>

          <p>
            3.4. Client acknowledges and agrees that Accenture shall be
            permitted to monitor the Client’s and its Authorized Users’ access
            and usage of the Services limited solely for the purposes of
            verifying Client’s compliance with the terms of this Agreement.
          </p>

          <p>
            3.5. Client hereby acknowledges, accepts and agrees that Accenture
            may have to suspend Client’s and/or its Authorized Users’ right to
            access or use all or any portion of the Platform Services, or remove
            any relevant Client Content as described below: (i) where such
            access or use, or any Client Content (x) poses a security risk to or
            may otherwise adversely impact the Services or (y) infringes or
            otherwise violates the rights or other interests of a third party,
            entails illegal or otherwise prohibited content or activities, or
            otherwise subjects Accenture to a potential liability; or (ii) where
            Accenture is required to do so under any applicable laws, or any
            court’s or governmental body’s order.{" "}
          </p>

          <p>
            3.6. When allowed under the applicable laws and if otherwise
            reasonable under the circumstances (as determined by Accenture in
            its discretion), Accenture shall provide Client with a written
            notice prior to such suspension, and an opportunity to take steps to
            avoid any such suspension. Any suspension of Client’s or its
            Authorized Users’ right to access or use the Services shall not
            release the Client from its obligations under this Agreement,
            including any obligation of paying the fees. Accenture’s suspension
            right is in addition to Accenture’s right to terminate this
            Agreement or any Service Order pursuant to Section 10.
          </p>

          <h2>4. CLIENT CONTENT</h2>

          <p>
            4.1. Client (and Client’s licensors, where applicable) own all
            right, title and interest in and to the Client Content.
          </p>

          <p>
            4.2. Client has obtained, and during the Term will obtain, all
            Consents required under applicable law prior to Client Content being
            entered into or processed by the Services. In particular, Client has
            collected and shall maintain and handle all Personal Information
            contained in Client Content in compliance with all applicable data
            privacy and protection laws, rules and regulations.
          </p>

          <p>
            4.3. Client authorizes Accenture to process its Personal Information
            in accordance to the applicable law and customer instructions, if
            any. . Client has satisfied itself that Accenture and any applicable
            Cloud Vendor have in place appropriate technical and organizational
            measures to protect Client Content (including Personal Information)
            processed on behalf of Client from unauthorized use or access,
            accidental loss, damage, destruction, theft or disclosure. If the
            Services involve the processing of Client Personal Information, then
            the terms of Exhibit B (GDPR Exhibit) shall also apply.
          </p>

          <p>
            4.4. Client grants to Accenture the nonexclusive right to host,
            store, process, modify and transfer the Client Content for the
            purposes of Accenture’s provision of, and Client’s use of, the
            Platform Services as set forth in this Agreement, and Accenture’s
            improvement of the Services incidental to such use. Accenture agrees
            to destroy any Client Content in accordance with Section 10.7 of
            this Agreement. Accenture may: (i) use usage patterns, trends,
            statistics, and other data derived from use of the Platform Services
            (but not Client Content itself) for purposes of developing or
            improving the Services and other Accenture products and services;
            and (ii) provide information to the Cloud Vendor regarding Client’s
            use of the Services, subject to the Cloud Vendor’s obligations of
            confidentiality, for Cloud Vendor’s use to improve its cloud
            infrastructure services.
          </p>

          <p>
            4.5. Client shall have the ability to access its Client Content
            hosted during the term of the Service. Client may export and
            retrieve its Client Content during such Service term, subject to any
            technical limitations in the Services or Client’s and its Authorized
            Users’ hardware and software, including factors such as (i) the size
            of Client’s instance of the Platform Services; and (ii) the
            frequency and/or timing of the export and retrieval.
          </p>

          <h2>5. FEES, PAYMENTS AND TAXES</h2>

          <p>
            5.1. Client shall pay the fees specified I the offer details of its
            Subscription. Fees are stated exclusive of all applicable duties,
            tariffs, and taxes. Unless otherwise specified, fees will be due in
            accordance with the subscription model acquired by Client. Late
            payments that are not the subject of a good faith dispute are
            subject to an interest charge, which is the lesser of: (a) one and
            one-half percent (1.5%) per month, or (b) the maximum legal rate.
          </p>

          <p>
            5.2. Client will be responsible for the payment of all taxes in
            connection with this Agreement including, but not limited to, sales,
            use, excise, value-added, business, service, goods and services,
            consumption, withholding, and other similar taxes or duties,
            including taxes incurred on transactions between and among
            Accenture, its Affiliates, and third party subcontractors. Client
            will reimburse Accenture for any deficiency relating to taxes that
            are Client’s responsibility under this Agreement. Each Party will be
            responsible for its own income taxes, employment taxes, and property
            taxes. The Parties agree to fully cooperate with each other to help
            enable each Party to accurately determine and reduce its own tax
            liability and to minimize any potential liability to the extent
            legally permissible. Each Party will provide to the other Party any
            resale exemption, multiple points of use certificates, treaty
            certification and other exemption information reasonably requested
            by the other Party.
          </p>

          <p>
            5.3. All amounts payable under this Agreement will be made without
            set-off or counterclaim, and without any deduction or withholding.
          </p>

          <h2>6. ACCENTURE’S INTELLECTUAL PROPERTY RIGHTS</h2>

          <p>
            6.1. Accenture (and its licensors, where applicable) own all right,
            title and interest, including all Intellectual Property Rights, in
            and to the systems, software and other content and materials used in
            the provision of the Services. In addition, Client agrees that
            Accenture (and its licensors, where applicable) will own all right,
            title and interest in and to any suggestions, ideas, enhancement
            requests, feedback, recommendations or other information provided by
            Client or any other Party relating to the Services, and Client
            hereby assigns any Intellectual Property Rights in such items to
            Accenture. The Accenture name, the Accenture logo, and the product
            names associated with the Services are trademarks of Accenture or
            third parties, and no right or license is granted to Client to use
            them.
          </p>

          <h2>7. WARRANTIES AND EXCLUSIONS</h2>

          <p>
            7.1. Accenture warrants that the Services provided to Client
            pursuant to this Agreement will comply in all material respects with
            the Documentation identified in the applicable Service Order. This
            warranty shall not apply where: (i) the Client’s or any Authorized
            User's access or use of the Services is not in accordance with this
            Agreement, the relevant Service Order (or any Exhibits to the
            relevant Service Order) or with Accenture’s instructions; (ii)
            modification or alteration of the Services or any systems, software
            or other content or materials embodied therein is made by any Party
            other than Accenture; or (iii) the Services are being provided free
            of charge, or as a trial, pre-release or as a beta release.
          </p>

          <p>
            7.2. Client shall provide Accenture with prompt written notice of
            any non-conformity of the Services, sufficiently describing such
            non-conformity, no later than within thirty (30) days of the
            appearance or Client’s discovery of such non-conformity.
          </p>

          <p>
            7.3. In such event, Accenture will use commercially reasonable
            efforts to correct any such non-conformity. In the event Accenture
            is unable to correct such non-conformity by exercising commercially
            reasonable efforts for a reasonable period, either Party may
            terminate the relevant Service Order concerning the non-conforming
            Service(s) on written notice to the other Party in which case as
            Client’s sole and exclusive remedy, Accenture will provide a
            pro-rated refund of any pre-paid Fees for periods after the
            effective date of termination.
          </p>

          <p>
            7.4. <b>Disclaimer</b>. EXCEPT AS EXPRESSLY PROVIDED IN THIS SECTION
            7, ACCENTURE, ITS AFFILIATES AND ITS LICENSORS MAKE NO
            REPRESENTATIONS AND PROVIDE NO WARRANTIES OF ANY KIND, WHETHER
            EXPRESS, IMPLIED, STATUTORY OR OTHERWISE REGARDING THE SERVICES OR
            ANY THIRD PARTY COMPONENTS OR CONTENT EMBODIED THEREIN, INCLUDING
            ANY WARRANTY THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR FREE, OR
            FREE OF HARMFUL COMPONENTS, OR THAT ANY CONTENT, INCLUDING CLIENT
            CONTENT OR THIRD PARTY COMPONENTS OR CONTENT, WILL BE SECURE OR NOT
            OTHERWISE LOST OR DAMAGED, AND INCLUDING ANY IMPLIED WARRANTIES OF
            MERCHANTABILITY, SATISFACTORY QUALITY, FITNESS FOR A PARTICULAR
            PURPOSE, NON-INFRINGEMENT, OR QUIET ENJOYMENT, AND ANY WARRANTIES
            ARISING OUT OF ANY COURSE OF DEALING OR USAGE OF TRADE. EACH PARTY,
            ITS AFFILIATES AND ITS LICENSORS DISCLAIM ALL, AND THE OTHER PARTY
            AGREES THAT IT IS NOT ENTITLED TO ANY EQUITABLE OR IMPLIED
            INDEMNITIES. THESE DISCLAIMERS SHALL ONLY APPLY TO THE EXTENT
            PERMITTED BY APPLICABLE LAW.
          </p>

          <p>
            7.5. <b>High Risk Activities</b>. Accenture, on behalf of itself and
            its Affiliates and licensors and the Cloud Vendor, specifically
            disclaims any express or implied warranty of fitness of the Services
            for High Risk Activities.
          </p>

          <h2>8. THIRD PARTY CLAIMS</h2>

          <p>
            8.1. Subject to the limitations in Section 9, Accenture will (i)
            defend (at its sole expense) Client and Client’s Affiliates against
            any claims brought against Client by any third party (that is not an
            Affiliate of Client) alleging that Client’s use of the Services, in
            accordance with the terms and conditions of the Agreement and the
            applicable Service Order, constitutes a direct infringement of a
            patent issued in the United States as of the Effective Date,
            copyright, or trade secret of any third party and (ii) pay the
            amount of any damages finally awarded against Client or the amount
            of any settlement agreed by Accenture.
          </p>

          <p>
            8.2. Accenture will have no obligations or liability under Section
            8.1 for any claims to the extent arising from:
          </p>
          <p>8.2.1. Client Content</p>
          <p>
            8.2.2. Client’s or any Authorized User’s use of the Services after
            Accenture has notified Client, in accordance with this Agreement, to
            discontinue such use and Client have been afforded a reasonable
            opportunity to discontinue such use;
          </p>
          <p>
            8.2.3. any unauthorized modification or unauthorized use of the
            Services where infringement or misappropriation would not have
            occurred but for such unauthorized modification or unauthorized use;
          </p>
          <p>
            8.2.4. any use of the Services, or any other act, by Client or an
            Authorized User, that is in breach of this Agreement, where the
            infringement or misappropriation would not have occurred but for the
            breach;
          </p>
          <p>
            8.2.5. any combination(s) of the Services with any other product,
            service, software, content, data or method not supplied by
            Accenture; or
          </p>
          <p>8.2.6. a free (no fee) or trial license of the Services.</p>

          <p>
            8.3. If any portion of the Services are, or in Accenture’s opinion
            are likely to be, held to be infringing, Accenture may choose (at
            its election and expense) to: (a) procure the rights to use the item
            alleged to be infringing; (b) replace the alleged infringing portion
            with a non-infringing equivalent; or (c) modify the alleged
            infringing portion to make it non-infringing while still providing
            substantially the same level of functionality. If Accenture
            determines the actions from Section 8.3 (a) to (c) are not
            commercially reasonable, Accenture may immediately terminate
            Client’s access to the Services.{" "}
          </p>

          <p>
            8.4. Client shall (i) defend (at its sole expense) Accenture and its
            Affiliates and licensors against claims brought against Accenture by
            any third party (that is not an Affiliate of Accenture) arising from
            or related to (a) any use of Services by Client or its Authorized
            Users in violation of any applicable law or regulation; or (b) any
            allegation that the Client Content violates, infringes or
            misappropriates the rights of a third party; (c) Client’s or its
            Authorized Users’ use of the Services or other act in violation of
            this Agreement or the relevant Service Order; or (d) Client’s or any
            Authorized User’s engaging in High Risk Activities, and (ii) pay the
            amount of any damages finally awarded against Accenture or the
            amount of any settlement agreed by Client. The foregoing shall apply
            regardless of whether such damage is caused by the conduct of Client
            and/or its Authorized Users or by the conduct of a third party using
            Client's or an Authorized User's access credentials where Client has
            negligently made the credentials available or chosen credentials
            that are easy to hack into.
          </p>

          <p>
            8.5. Client will have no obligations of liability under Section 8.4
            for any claims arising from:
          </p>

          <p>
            8.5.1. Client Content after Client has notified Accenture, in
            accordance with the terms of this Agreement, to delete the Client
            Content from the SaaS and Accenture has been afforded a reasonable
            opportunity to do so; or
          </p>

          <p>
            8.5.2. any unauthorized access or use of the Client Content by
            Accenture that is in breach of this Agreement, where the
            infringement or misappropriation would not have occurred but for
            such breach.
          </p>

          <p>
            8.6. In connection with any third party claims pursuant to Section
            8.1 or 8.4, the indemnified Party will (a) give the indemnifying
            Party prompt written notice of the claim; (b) reasonably cooperate
            with the indemnifying Party (at the indemnifying Party’s expense) in
            connection with the defense and settlement of such claim, and (c)
            grant the indemnifying Party sole control of the defense and
            settlement of the claim, except that the indemnifying Party may not
            consent to the entry of any judgment or enter into any settlement
            with respect to the claim without the indemnified Party’s prior
            written consent unless the settlement or judgment is purely
            financial, is paid entirely by the indemnifying Party, is
            confidential, does not require the indemnified Party to admit to any
            fault or wrongdoing, and fully releases the indemnified Party from
            any and all further claims or causes of action relating to the
            subject matter of the claim. The non-controlling Party may, at its
            expense, participate in the defense and settlement of the claim with
            counsel of its own choosing. If the indemnifying Party fails to
            assume control within 30 days of written notice of the claim, the
            indemnified Party may assume control of the defense of the claim
          </p>

          <p>
            8.7. <b>Exclusive Remedy</b>. SECTION 8.1 CONSTITUTES CLIENT’S SOLE
            AND EXCLUSIVE REMEDY AND ACCENTURE’S (AND ITS AFFILIATES’) ENTIRE
            OBLIGATION TO CLIENT WITH RESPECT TO ANY CLAIM THAT THE SERVICES
            INFRINGE OR MISAPPROPRIATE THE INTELLECTUAL PROPERTY RIGHTS OF ANY
            THIRD PARTY.
          </p>

          <h2>9. LIMITATION OF LIABILITY</h2>

          <p>
            9.1. EXCEPT FOR (I) THE PARTIES’ OBLIGATIONS UNDER SECTION 8;] (II)
            DAMAGES RESULTING FROM DEATH OR BODILY INJURY ARISING FROM EITHER
            PARTY’S GROSS NEGLIGENCE OR WILLFUL MISCONDUCT OR FROM FRAUD OR
            FRAUDULENT MISREPRESENTATION; AND (III) ACCENTURE’S RIGHT TO COLLECT
            UNPAID FEES DUE HEREUNDER, UNDER NO CIRCUMSTANCES AND REGARDLESS OF
            THE NATURE OF ANY CLAIM SHALL EITHER PARTY (OR THEIR RESPECTIVE
            AFFILIATES OR ACCENTURE’S LICENSORS) BE LIABLE TO EACH OTHER OR ANY
            OTHER PERSON OR ENTITY UNDER THIS AGREEMENT FOR AN AMOUNT OF DAMAGES
            IN EXCESS OF THE FEES PAID UNDER A SERVICE ORDER FOR THE APPLICABLE
            SERVICES IN THE TWELVE (12) MONTH PERIOD PRECEDING THE DATE OF THE
            INCIDENT GIVING RISE TO LIABILITY OR 100 USD IF THERE IS NO RELATED
            FEES APPLICABLE TO THE SERVICES.
          </p>

          <p>
            9.2. TO THE EXTENT PERMITTED BY LAW NEITHER PARTY NOR ANY OF EITHER
            PARTY’S RESPECTIVE AFFILIATES WILL BE LIABLE TO THE OTHER PARTY
            UNDER ANY CAUSE OF ACTION OR THEORY OF LIABILITY, EVEN IF A PARTY
            HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES;
            BUSINESS INTERRUPTION, LOSS OF PROFITS OR SAVINGS, REVENUES, OR
            GOODWILL; LOSS OR CORRUPTION OF DATA; UNAVAILABILITY OF ANY OR ALL
            OF THE PLATFORM SERVICES; INVESTMENTS, EXPENDITURES OR COMMITMENTS
            RELATED TO USE OF OR ACCESS TO THE PLATFORM SERVICES; COST OF
            PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; UNAUTHORIZED ACCESS TO,
            COMPROMISE, ALTERATION OR LOSS OF CLIENT CONTENT OR BUSINESS
            INFORMATION; OR COST OF REPLACEMENT OR RESTORATION OF ANY LOST OR
            ALTERED CLIENT CONTENT.
          </p>

          <p>
            9.3. The Parties acknowledge that the limitations on liability set
            out in this Section 9 are essential terms of this Agreement, and the
            Parties would not have entered this Agreement without them.
          </p>

          <p>
            9.4. No action, regardless of form, arising out of this Agreement
            may be brought by Client more than two (2) years after Client knew
            or should have known of the event which gave rise to the cause of
            action.{" "}
          </p>

          <h2>10. TERM AND TERMINATION</h2>

          <p>
            10.1. The term of this Agreement will commence on the Effective Date
            and will continue until expiration of the Subscription and its
            relevant term.
          </p>

          <p>
            10.2. Accenture may terminate this Agreement upon [thirty(30)] days'
            advance written notice if Client commits any material breach of this
            Agreement and fails to cure such default (if curable) within the
            [thirty (30)] day period, including if there has been any such act
            or omission by Client or any Authorized User that has given
            Accenture the right to suspend the provision of Services in
            accordance with Section 3.6.
          </p>

          <p>
            10.3. Accenture may also terminate this Agreement and/or any Service
            Order without liability upon [thirty (30)] days’ notice to Client or
            any Authorized User: (i) in the event where an underlying contract
            between Accenture and the Cloud Vendor concerning the provision of
            the IaaS terminates, or (ii) if termination of the Agreement and/or
            any Service is necessary to comply with applicable law or binding
            requests of governmental entities.
          </p>

          <p>
            10.4. Upon the effective date of expiration or termination of the
            Subscription, Client shall cease Client’s and its Authorized Users’
            access to and use of the Services.
          </p>

          <p>
            10.5. Accenture will destroy, procure the destruction of, or
            otherwise dispose of any Client Content in its possession or in
            possession of a Cloud Vendor (including any data back-ups) upon
            expiration or termination of the applicable Service Order.
          </p>

          <h2>11. CONFIDENTIALITY</h2>

          <p>
            11.1. Each Party agrees that it will use the other Party’s
            Confidential Information only to the extent reasonably necessary for
            purposes of this Agreement or a Service Order. Each Party agrees to
            take reasonable steps to protect the other’s Confidential
            Information, provided that these steps must be at least as
            protective as those the Party takes to protect its own Confidential
            Information of similar nature, but in any event no less than a
            reasonable standard of care.
          </p>

          <p>
            11.2. Each Party may disclose the other Party’s Confidential
            Information to its Affiliates, employees, contractors, licensors and
            agents who: (a) have a need to know it for purposes contemplated by
            this Agreement, and (b) are legally bound to protect the
            Confidential Information on terms no less protective than the terms
            of this Agreement.
          </p>

          <p>
            11.3. This Section 11 will not apply to any information that either
            Party can demonstrate: (a) was at the time of disclosure or that has
            become thereafter published or otherwise becomes publicly available
            at no fault of the receiving Party; (c) was in the possession of the
            receiving Party at the time of disclosure to it and was not the
            subject of a pre-existing confidentiality obligation; (d) was
            received after disclosure to it from a third party who had a lawful
            right to disclose such information (without corresponding
            confidentiality obligations) to it; or (e) was independently
            developed by the receiving Party without use of the Confidential
            Information of the disclosing Party.
          </p>

          <p>
            11.4. The receiving Party will not be considered to have breached
            its obligations under this Section 11 for disclosing Confidential
            Information of the disclosing Party to the extent required to
            satisfy any legal requirement of a competent governmental or
            regulatory authority, provided that promptly upon receiving any such
            request and to the extent that it may legally do so, the receiving
            Party: (i) advises the disclosing Party prior to making such
            disclosure in order that the disclosing Party may object to such
            disclosure or take any other action that it considers appropriate to
            protect the Confidential Information and; (ii) takes actions
            necessary to minimize any disclosure to only that necessary to
            satisfy any legal requirement of a competent governmental or
            regulatory authority (including through redaction of sensitive
            commercial information, where legally permissible).
          </p>

          <p>
            11.5. Any reproduction of any Confidential Information of the other
            Party shall remain the property of the disclosing Party, and the
            disclosing Party may, at any time including on termination or
            expiration of this Agreement, request the receiving Party to return,
            destroy or delete (and confirm the destruction or deletion of the
            same) as instructed (in such a manner that it cannot be recovered)
            all Confidential Information of the disclosing Party in the
            receiving Party’s possession or control. Notwithstanding the
            foregoing, each Party may archive all copies of Confidential
            Information that it is required to retain to comply with law and its
            other record-keeping requirements.
          </p>

          <p>
            11.6. The Customer shall not disclose the prices discussed between
            the parties to third parties, unless otherwise agreed by the
            Parties.. Neither Party will use the name of the other Party without
            the prior written consent of the other, except that Client agrees
            that Accenture may use Client's name in customer listings or
            quarterly calls with its investors or, at times mutually agreeable
            to the Parties, as part of Accenture's marketing efforts (including
            reference calls and stories, press testimonials, and prospective
            customer meetings).
          </p>

          <h2>12. ADDITIONAL TERMS</h2>

          <p>
            12.1. <b>Compliance with Laws.</b> Accenture will comply with all
            laws applicable to it and its business in providing Client with
            access to the Services, and Client will comply with all laws
            applicable to it and its business in providing the Client Content
            and in using the Services in the manner. Each Party will comply with
            relevant data privacy laws to the extent applicable to such Party in
            its role with respect to the processing of Client Personal
            Information under this Agreement. Each Party will comply with all
            export control and economic sanctions laws applicable to its
            performance under this Agreement. Client agrees that Client will and
            will procure that Authorized Users do not use the Services in or in
            relation to any activities involving a country subject to
            comprehensive economic sanctions (including without limitation Cuba,
            Iran, North Korea, Sudan, Syria or the Crimea region of Ukraine), or
            involving a Party in violation of such applicable trade control
            laws, or that require government authorization, without first
            obtaining the informed consent of Accenture and the required
            authorization. For the avoidance of doubt, Client shall not grant
            access to the Services to any individual, entity or organization
            which is subject to trade sanctions or embargos by the United States
            or any applicable jurisdiction, including any individual, entity or
            organization which is listed on the OFAC Specially Designated
            Nationals List from time to time.
          </p>

          <p>
            12.2. <b>Notices</b>. Unless expressly stated otherwise in this
            Agreement, all notices under this Agreement must be in writing and
            must be delivered personally, sent by certified mail (return receipt
            requested); or sent by express courier (with confirmation of
            delivery). The notice will be deemed given and will be effective
            upon receipt: (a) when it is delivered to a Party personally; (b)
            upon receipt if sent certified mail, return receipt requested; or
            (c) when delivered by a nationally recognized overnight courier
            service such as FedEx (with confirmation of delivery). Any notice by
            email will only be allowed in the Sections of this Agreement that
            expressly permit it. All notices must be addressed to the other
            Party at the address informed by the entity or person accessing the
            Solutions or set forth in the preamble above.
          </p>

          <p>
            12.3. <b>Dispute Resolution, Governing Law and Jurisdiction</b>. The
            Parties will make good faith efforts to first resolve internally
            within 30 days any dispute, including over an invoice, in connection
            with this Agreement by escalating it to higher levels of management.
            This Agreement shall be ruled by Brazilian legislation. The Parties
            choose the venue of the City of São Paulo, State of São Paulo, and
            waive any other venues irrespective of how privileged they may be,
            to settle any issues that may arise from this Agreement.
          </p>

          <p>
            12.4. <b>Force Majeure</b>. Other than Client’s obligation to pay
            Accenture pursuant to this Agreement and any Service Order, neither
            Party will be liable for any delay or failure to perform any
            obligation under this Agreement where the delay or failure results
            from any cause beyond its reasonable control, including but not
            limited to electrical or power outage, utilities or
            telecommunications failures, earthquake, storms or other elements of
            nature, blockages, embargoes, riots, acts or orders of government,
            acts of terrorism or war, or labour disputes with its or its
            affiliates’ employees, industrial disturbances.
          </p>

          <p>
            12.5. <b>Survival</b>. The provisions of Sections 6, 7.4, 8, 9, 11
            and 12, and any other Sections which by their nature are intended to
            survive, will survive the termination or expiration of this
            Agreement.
          </p>

          <p>
            12.6. <b>Assignment</b>. Client may not assign this Agreement or
            delegate or sublicense any of Client’s rights or obligations
            hereunder, including by operation of law, without the prior written
            consent of Accenture and any attempt to do so in violation of this
            provision will be null and void. This Agreement will be binding upon
            and inure to the benefit of the Parties and their respective
            successors and permitted assigns.
          </p>

          <p>
            12.7. <b>Variation</b>. Except as otherwise expressly provided to
            the contrary in this Agreement, this Agreement may only be changed,
            modified or expanded by a writing signed by both Parties.
          </p>

          <p>
            12.8. <b>No waiver</b>. No delay, neglect or forbearance on the part
            of either Party in enforcing against the other any term or condition
            of this Agreement will be deemed to be a waiver nor will it in any
            way prejudice any right of that Party under this Agreement nor will
            any single or partial exercise of any right preclude any other or
            further exercise of these rights or any other right.
          </p>

          <p>
            12.9. <b>Severability</b>. The invalidity or unenforceability of any
            provision of this Agreement will not affect the validity or
            enforceability of any other provision hereof and it is the intent
            and agreement of the Parties that this Agreement will be deemed
            amended by modifying such provision to the extent necessary to
            render it valid, legal and enforceable while preserving its intent
            or, if such modification is not possible, by substituting another
            provision that is legal and enforceable and that achieves the same
            objective. The Parties agree that any principle of construction or
            rule of law that provides that an agreement will be construed
            against the drafter will not apply to this Agreement.
          </p>

          <p>
            12.10. <b>Entire Agreement</b>. This Agreement, along with any
            applicable Service Orders exhibits or other attachments hereto,
            contains the entire agreement between the Parties with respect to
            the matters specified herein and supersedes all prior or
            contemporaneous representations, understandings, agreements or
            communications between Accenture and Client, whether written or
            verbal, regarding the subject matter of this Agreement. Except as a
            Service Order may otherwise expressly provide, each Service Order
            will be a complete statement of its subject matter and will
            supplement and modify the terms and conditions of this Agreement for
            purposes of that Service Order only.
          </p>

          <p>
            12.11. <b>Relationship of the Parties</b>. Nothing in this Agreement
            will be deemed to create a joint venture, partnership, or agency
            relationship between the Parties or be deemed to authorize either
            Party to incur any liabilities or obligations on behalf of, or in
            the name of, the other.
          </p>

          <p>
            12.12. <b>Third Party Rights</b>. Accenture’s licensors are third
            party beneficiaries hereunder. This Agreement does not otherwise
            create any third party beneficiary rights, and, in particular, it is
            agreed that Client’s Authorized Users are not third party
            beneficiaries. Accenture and Client are independent contractors.
          </p>

          <p>
            12.13. <b>Existing Agreements</b>. This Agreement is separate from
            any obligations, rights, or requirements contained in any existing
            master services agreement, professional services agreement,
            consulting services agreement, outsourcing agreement, or related
            agreements (“Existing Agreements”) between the Parties. Any Existing
            Agreement will not apply to the products and services that are the
            subject of this Agreement, and this Agreement expressly overrides
            any conflicting statements in any Existing Agreement.
          </p>

          <p>
            12.14. <b>Counterparts</b>. This Agreement may be executed in any
            number of counterparts and executed by other electronic
            communication as agreed upon by the Parties, such execution to be
            considered an original for all purposes, and all of which together
            will constitute one and the same instrument, notwithstanding that
            the Parties may not both be signatories to the original or same
            counterpart.
          </p>

          <p>
            12.15. <b>Business Contact Information</b>. Each party consents to
            the other party using its Business Contact Information for contract
            management, payment processing, service offering, and business
            development purposes related to this Agreement and such other
            purposes as set out in the using party’s global data privacy policy
            (copies of which shall be made available upon request). For such
            purposes, and notwithstanding anything else set forth in this
            Agreement with respect to Client Personal Information in general,
            each party shall be considered a data controller with respect to the
            other party’s Business Contact Information and shall be entitled to
            transfer such information to any country where such party’s global
            organization operates.
          </p>

          <h2>EXHIBIT A – DEFINITIONS</h2>

          <p>
            <b>“Additional Services”</b> has the meaning given to it in Section
            2;
          </p>

          <p>
            <b>“Affiliate”</b> of a Party means any entity, whether incorporated
            or not, that is Controlled by, Controls, or is under common Control
            with such Party. “Control” means the ability, whether directly or
            indirectly, to direct the affairs of another by means of ownership,
            contract or otherwise;
          </p>

          <p>
            <b>“Agreement”</b> means this Accenture Subscription Services
            Agreement, any exhibits attached hereto and any other documents
            which may be established between the parties.
          </p>

          <p>
            <b>“Authorized User”</b> means any individual or entity that,
            directly or indirectly through another Authorized User, accesses or
            uses the Services;
          </p>

          <p>
            <b>“Business Contact Information”</b> means the names, mailing
            addresses, email addresses, and phone numbers of a party’s
            personnel, officers and directors and, with respect to Client, such
            information regarding Client’s vendors and customers that Accenture
            may have access to in maintaining the parties’ business
            relationship.
          </p>

          <p>
            <b>“Cloud Vendor”</b> means the relevant cloud service provider,
            providing the IaaS;
          </p>

          <p>
            <b>“Client Content”</b> means all content, materials, data and
            information, including Personal Information that Client or its
            Authorized Users enter into the Platform Services. Client Content
            shall not include any component of the Platform Services or material
            or data provided by or on behalf of Accenture or its licensors.{" "}
          </p>

          <p>
            <b>"Client Personal Information”</b> means Personal Information
            provided to Accenture by or on behalf of Client in connection with
            this Agreement, in the form of Client Content;
          </p>

          <p>
            <b>“Confidential Information”</b> means information that relates to
            the other Party’s (or to Cloud Vendor's) past, present, or future
            research, development, business activities, products, services, and
            technical knowledge, which is identified by the discloser as
            confidential or that would be understood to be confidential by a
            reasonable person under the circumstances. For the purposes of this
            Agreement, the term “Confidential Information” does not include
            Client Content;
          </p>

          <p>
            <b>“Consent”</b> means (1) all consents, permissions, notices and
            authorizations necessary for Accenture to provide the Platform
            Services, including any of the foregoing from Client employees or
            third parties; (2) valid consents from or notices to applicable
            individuals whose data is processed by the Platform Service); and
            (3) required authorizations from regulatory authorities, employee
            representative bodies or other applicable third parties;
          </p>

          <p>
            <b>“Effective Date”</b> is the date that is Client’s initial access
            to the Services through https://fixr.accenture.com or the date we
            provide you with confirmation of your Subscription or the date on
            which your Subscription is renewed, as applicable;
          </p>

          <p>
            <b>“Fees”</b> means the fees specified in the relevant subscription
            plan acquired and payable in accordance with Section 6;
          </p>

          <p>
            <b>“High Risk Activities”</b> means Usage such as the operation of
            nuclear facilities, aircraft navigation or communication systems,
            air traffic control, direct life support machines, or weapons
            systems environments where the use or failure of the Service could
            lead to death, personal injury or environmental damage;
          </p>

          <p>
            <b>“IaaS”</b> means the infrastructure-as-a-service provided by a
            Cloud Vendor on which the Services are hosted or is otherwise
            utilized in the provision of the Services by Accenture;
          </p>

          <p>
            <b>“Intellectual Property Rights”</b> means unpatented inventions,
            patent applications, patents, design rights, copyrights, trademarks,
            service marks, trade names, domain name rights, mask work rights,
            know-how and other trade secret rights, and all other intellectual
            property rights, derivatives thereof, and forms of protection of a
            similar nature;
          </p>

          <p>
            <b>“Permitted Purpose”</b> has the meaning given to such term in the
            applicable Service Order. If the applicable Service Order does not
            define Permitted Purpose, it will mean Client’s internal business
            purposes.
          </p>

          <p>
            <b>“Personal Information”</b> means data which names or identifies a
            natural person including, without limitation: (a) data that is
            explicitly defined as a regulated category of data under data
            privacy laws applicable to Client; (b) non-public personal data,
            such as national identification number, passport number, social
            security number, driver’s license number; (c) health or medical
            information, such as insurance information, medical prognosis,
            diagnosis information or genetic information; (d) financial
            information; and/or (e) sensitive personal data, such as race,
            religion, marital status, disability, or sexuality;
          </p>

          <p>
            <b>“Services”</b> means a hosted solution made available by
            Accenture for Client’s access and use on a subscription basis, as
            detailed in the applicable subscription plan acquired by Client. The
            term “Services” includes any modifications, enhancements, additions,
            extensions, translations and derivative works thereof and any
            configuration and related services. The Platform Services do not
            include Client Content or any Client-provided third party software;
          </p>

          <p>
            <b>“Term”</b> has the meaning set forth in Section 10;
          </p>

          <ul>
            <p>
              <b>"Virus"</b> means any item, software, device or code which is
              intended by any person to, or which is likely to, or which may:
            </p>

            <p>(a) impair the operation of any software or computer systems;</p>

            <p>
              (b) cause loss of, or corruption or damage to any software or
              computer systems or data;
            </p>

            <p>
              (c) prevent access to or allow unauthorised access to any software
              or computer system or data; and / or
            </p>

            <p>(d) damage the reputation of the Client and / or Accenture,</p>

            <p>
              including any computer virus, Trojan horse, worm, software bomb,
              authorization key, license control utility or software lock.{" "}
            </p>
          </ul>

          <h2>EXHIBIT B – DATA PRIVACY TERMS</h2>
          <p>
            <b>RESERVED</b>
          </p>

          <h2>SCHEDULE 2</h2>
          <h3>Cloud Vendor Terms</h3>

          <a
            href="https://www.microsoft.com/en-gb/Licensing/product-licensing/products.aspx"
            target="_blank"
            rel="noreferrer"
          >
            <p>
              <b>
                Microsoft Azure Policies, as they may be updated from time to
                time:
                https://www.microsoft.com/en-gb/Licensing/product-licensing/products.aspx
              </b>
            </p>
          </a>
        </Box>
      </Flex>
    </Container>
  );
};

// @ts-ignore
TermsOfService.public = true;

export default TermsOfService;
